import React from "react";
import {
  BarChart, Bar, Cell,
  RadialBarChart, RadialBar,
  Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";
import { useIntl } from "react-intl";
import styles from "../styles/AdminStats.module.scss";
import { PatternStats } from "../types/adminStats.type";


interface PatternsSectionProps {
  data: PatternStats | null;
}

const COLORS = {
  blue:   "#4f6ef7",
  green:  "#3ecf8e",
  orange: "#f7934f",
  purple: "#7c4ff7",
};

const BAR_PALETTE = [
  "#4f6ef7", "#3ecf8e", "#f7934f", "#7c4ff7", "#f74f6a",
  "#4fc9f7", "#f7e44f", "#8ef74f", "#f74fc0", "#4ff7a0",
];

function ChartTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      {payload.map((p: any) => (
        <div key={p.name} className={styles.tooltipRow}>
          <span className={styles.tooltipDot} style={{ background: p.color || p.fill }} />
          <span className={styles.tooltipName}>{p.name}:</span>
          <span className={styles.tooltipValue}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function PatternsSection({ data }: PatternsSectionProps): React.ReactElement {
  const intl = useIntl();
  const totalPatterns = data?.totalPatterns ?? data?.topPatterns?.length ?? 0;

  const patternRadialData = data
    ? [
        { name: intl.formatMessage({ id: "admin.stats.patterns.avgPrice", defaultMessage: "Avg Price" }),      value: Math.round(data.averagePrice), fill: COLORS.orange },
        { name: intl.formatMessage({ id: "admin.stats.patterns.totalPatterns", defaultMessage: "Total Patterns" }), value: totalPatterns,                  fill: COLORS.blue   },
      ]
    : [];

  // Top 5 patterns by totalSales for the bar chart
  const topBarData = data?.topPatterns?.slice(0, 5).map((p, i) => ({
    name: p.name.length > 16 ? p.name.slice(0, 14) + "…" : p.name,
    sales: p.totalSales,
    color: BAR_PALETTE[i],
  })) ?? [];

  return (
    <>
      {/* ── Stat cards ── */}
      <div className={`ui three stackable cards ${styles.cardRow}`}>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.patterns.totalPatterns", defaultMessage: "Total Patterns" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.blue }}>
              {totalPatterns || "—"}
            </div>
          </div>
        </div>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.patterns.averagePrice", defaultMessage: "Average Price" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.orange }}>
              {data ? `$${data.averagePrice.toFixed(2)}` : "—"}
            </div>
          </div>
        </div>
        <div className={`ui card ${styles.statCard}`}>
          <div className="content">
            <div className={styles.cardLabel}>{intl.formatMessage({ id: "admin.stats.patterns.topPatternSales", defaultMessage: "Top Pattern Sales" })}</div>
            <div className={styles.cardValue} style={{ color: COLORS.green }}>
              {data?.topPatterns?.[0]
                ? `$${data.topPatterns[0].totalSales.toLocaleString()}`
                : "—"}
            </div>
            <div className={styles.cardSub}>
              {data?.topPatterns?.[0]?.name ?? ""}
            </div>
          </div>
        </div>
      </div>

      {/* ── Charts ── */}
      <div className={styles.chartsRow}>

        {/* Radial bar */}
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.patterns.atGlance", defaultMessage: "Patterns at a Glance" })}</div>
          <ResponsiveContainer width="100%" height={220}>
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={100}
              data={patternRadialData}
              startAngle={180}
              endAngle={-180}
            >
              <RadialBar
                dataKey="value"
                cornerRadius={6}
                background={{ fill: "#f0f2f5" }}
              />
              <Tooltip content={<ChartTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                formatter={(val) => (
                  <span className={styles.legendLabel}>{val}</span>
                )}
              />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>

        {/* Bar — top 5 patterns by sales */}
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.patterns.top5BySales", defaultMessage: "Top 5 Patterns by Sales" })}</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={topBarData}
              barSize={32}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            >
              <Tooltip
                content={<ChartTooltip />}
                cursor={{ fill: "rgba(79,110,247,0.06)" }}
              />
              <Bar dataKey="sales" radius={[6, 6, 0, 0]}>
                {topBarData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className={styles.barLabels}>
            {topBarData.map((d) => (
              <div key={d.name} className={styles.barLabel}>
                <span className={styles.barDot} style={{ background: d.color }} />
                {d.name}
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── Top Patterns Table ── */}
      {data?.topPatterns?.length ? (
        <div className={`ui segment ${styles.chartCard}`}>
          <div className={styles.chartTitle}>{intl.formatMessage({ id: "admin.stats.patterns.topPatterns", defaultMessage: "Top Patterns" })}</div>
          <table className="ui very basic compact table">
            <thead>
              <tr>
                <th>#</th>
                <th>{intl.formatMessage({ id: "admin.stats.patterns.patternName", defaultMessage: "Pattern Name" })}</th>
                <th>{intl.formatMessage({ id: "admin.stats.patterns.totalSales", defaultMessage: "Total Sales" })}</th>
                <th>{intl.formatMessage({ id: "admin.stats.patterns.orders", defaultMessage: "Orders" })}</th>
                <th>{intl.formatMessage({ id: "admin.stats.patterns.avgSale", defaultMessage: "Avg. Sale" })}</th>
              </tr>
            </thead>
            <tbody>
              {data.topPatterns.map((pattern, i) => {
                const avg = pattern.totalOrders > 0
                  ? (pattern.totalSales / pattern.totalOrders).toFixed(2)
                  : "0.00";
                return (
                  <tr key={pattern._id}>
                    <td style={{ color: "#888", fontWeight: 600 }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{pattern.name}</td>
                    <td style={{ color: COLORS.green, fontWeight: 600 }}>
                      ${pattern.totalSales.toLocaleString()}
                    </td>
                    <td style={{ color: COLORS.blue }}>{pattern.totalOrders}</td>
                    <td style={{ color: COLORS.orange }}>${avg}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}